import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { RouterModule, Route } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { RavenErrorLoggingService } from '@nuso/common/util-errors';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client';
import type { LocalState } from '@apollo/client/local-state';
import {
  createApollo,
  createInitialNetworkStatusState,
  NetworkStatusUpdateService,
} from '@nuso/common/data-access-api';
import { createInitialLoginState } from '@nuso/auth/data-access';
import { createRavenErrorLoggingService } from '@nuso/common/util-errors';
import { environment } from '../environments/environment';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoginStateManagementService } from '@nuso/auth/data-access';
import * as Sentry from '@sentry/angular';
import {
  appEnvironmentToken,
  AppEnvironment,
} from '@nuso/portal/environments';
import { pricebookUsageTypes } from '@nuso/quoting/util';
import { NetworkLostInterceptor, XTabIdInterceptor } from '@nuso/common/data-access-api';

if (environment.sentry) {
  Sentry.init({
    dsn: environment.sentry,
    environment: environment.envName,
    release: window['MARKETSPACE_VERSION'],
  });
}

const clientResolvers: LocalState.Resolvers = {
  Pricebook: {
    usageTypes: pricebookUsageTypes,
  },
};

const extraOptions = {
  resolvers: clientResolvers,
};

const mainRoute: Route = {
  path: '',
  loadChildren: () =>
    import('@nuso/quoting/feature-shell').then(
      (m) => m.QuotingFeatureShellModule
    ),

  // You could put authentication guards here to run checks before lazy-loading main
  canLoad: [],
  canActivate: [],
};

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function createInitialAppState() {
  return {
    ROOT_QUERY: {
      ...createInitialNetworkStatusState(),
      ...createInitialLoginState(),
    },
  };
}

const appEnvironment: AppEnvironment = {
  envName: environment.envName,
  appName: environment.appName,
  production: environment.production,
  sentry: environment.sentry,
  showDebug: environment.showDebug,
  statusPageIoEmbedUrl: environment.statusPageIoEmbedUrl,
};

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        RouterModule.forRoot([mainRoute], { initialNavigation: 'enabledBlocking' }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        BrowserAnimationsModule,
        MatSnackBarModule], providers: [
        // CDK 21 top-layer overlays open body-appended ng-select dropdowns behind dialogs.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        // apollo-angular 10 removed ApolloModule, which only provided Apollo.
        Apollo,
        { provide: 'IS_PRODUCTION', useValue: environment.production },
        { provide: 'EXTRA_OPTIONS', useValue: extraOptions },
        { provide: appEnvironmentToken, useValue: appEnvironment },
        {
            provide: InMemoryCache,
            useValue: new InMemoryCache().restore(createInitialAppState()),
        },
        {
            provide: RavenErrorLoggingService,
            useValue: createRavenErrorLoggingService(environment.production),
        },
        {
            provide: APOLLO_OPTIONS,
            useFactory: createApollo,
            deps: [
                HttpLink,
                InMemoryCache,
                NetworkStatusUpdateService,
                'IS_PRODUCTION',
                'EXTRA_OPTIONS',
            ],
        },
        { provide: HTTP_INTERCEPTORS, useClass: XTabIdInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: NetworkLostInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  private apollo = inject(Apollo);
  private cache = inject(InMemoryCache);
  private loginStateService = inject(LoginStateManagementService);
  private translate = inject(TranslateService);

  constructor() {
    this.apollo.client.onClearStore(() => {
      this.cache.restore(createInitialAppState());
      return Promise.resolve();
    });

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    //  TODO: we may change this based on the user, or perhaps the brand...
    this.translate.use('en');

    this.loginStateService.init();
  }
}
